import React, { useState, useRef, useEffect } from "react";
import "./FilterDropdown.scss";
import { getConsumeDataByFilterId } from "../../../services/DashboardConfigurationService";

export const FilterDropdown = ({
  filterData,
  groupData,
  isGroup,
  returnValues,
  clearAllTrigger,
  onApplyFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const [filterValues, setFilterValues] = useState({
    filterID: filterData?.filter?.id,
    filterCompositions: [],
    filterType: "dropdown",
    selectionTimeMode: "string", // Para dropdown siempre es string
  });

  // Obtener configuración del dropdown
  const dropdownConfig = filterData?.filter?.filterDropdownConfig;
  const isMultipleSelection = dropdownConfig?.selectionType === "multiple";
  const autoSelectFirstValue = dropdownConfig?.autoSelectFirstValue;
  const autoHideWhenNoRows = dropdownConfig?.autoHideWhenNoRows;

  // Llamar al servicio cuando el componente se monta
  useEffect(() => {
    const fetchFilterData = async () => {
      if (!filterData?.id) {
        console.warn("No filter ID provided");
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        console.log("Fetching data for filter:", filterData);
        const dataResponse = await getConsumeDataByFilterId(
          filterData.filter.id
        );
        console.log("DATA DROPDOWN", dataResponse);

        // Transformar la respuesta del servicio basándose en la configuración del filtro
        if (dataResponse && dataResponse.data && dataResponse.columns) {
          const { columns, data } = dataResponse;
          const columnForValue = dropdownConfig?.columnForValue;
          const columnForLabel = dropdownConfig?.columnForLabel;

          // Encontrar los índices de las columnas basándose en la configuración
          const valueColumnIndex = columnForValue
            ? columns.indexOf(columnForValue)
            : 0;
          const labelColumnIndex = columnForLabel
            ? columns.indexOf(columnForLabel)
            : 1;

          // Verificar que los índices sean válidos
          const finalValueIndex = valueColumnIndex >= 0 ? valueColumnIndex : 0;
          const finalLabelIndex =
            labelColumnIndex >= 0
              ? labelColumnIndex
              : columns.length > 1
              ? 1
              : 0;

          const transformedOptions = data.map((row) => ({
            value: row[finalValueIndex], // Columna configurada para value
            label: row[finalLabelIndex] || row[finalValueIndex], // Columna configurada para label, o usar value si no hay label
          }));

          console.log("Mapping configuration:", {
            columnForValue,
            columnForLabel,
            valueColumnIndex: finalValueIndex,
            labelColumnIndex: finalLabelIndex,
            columns,
            transformedOptions: transformedOptions.slice(0, 3), // Solo mostrar las primeras 3 para debug
          });

          setOptions(transformedOptions);
        } else {
          console.warn("Invalid service response structure", dataResponse);
          setOptions([]);
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
        setOptions([]); // Usar array vacío en caso de error
      } finally {
        setLoading(false);
      }
    };

    fetchFilterData();
  }, [isOpen]);

  // Filtrar opciones basado en la búsqueda
  const filteredOptions = options.filter((option) => {
    const labelStr = String(option.label || "").toLowerCase();
    const valueStr = String(option.value || "").toLowerCase();
    const searchStr = searchValue.toLowerCase();

    return labelStr.includes(searchStr) || valueStr.includes(searchStr);
  });

  // Manejar click fuera del dropdown para cerrarlo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-seleccionar el primer valor si está configurado
  useEffect(() => {
    if (
      autoSelectFirstValue &&
      options.length > 0 &&
      selectedOptions.length === 0
    ) {
      const firstOption = options[0];
      if (firstOption && firstOption.value) {
        setSelectedOptions([firstOption.value]);
      }
    }
  }, [options, dropdownConfig?.autoSelectFirstValue, selectedOptions.length]);

  // Escuchar el trigger de clear all desde el padre
  useEffect(() => {
    if (clearAllTrigger > 0) {
      handleClearAll(true); // true indica que viene del padre
    }
  }, [clearAllTrigger]);

  // Manejar selección de opciones
  const handleOptionToggle = (optionValue) => {
    let newSelectedOptions;

    if (isMultipleSelection) {
      // Comportamiento para múltiple selección
      setSelectedOptions((prev) => {
        if (prev.includes(optionValue)) {
          newSelectedOptions = prev.filter((val) => val !== optionValue);
        } else {
          newSelectedOptions = [...prev, optionValue];
        }

        // Actualizar filterValues y notificar al padre después del cambio
        updateFilterValuesAndNotifyParent(newSelectedOptions);

        return newSelectedOptions;
      });
    } else {
      // Comportamiento para selección única
      if (selectedOptions.includes(optionValue)) {
        // Si ya está seleccionado, lo deseleccionamos (como si fuera toggle)
        newSelectedOptions = [];
      } else {
        // Si no está seleccionado, lo seleccionamos
        newSelectedOptions = [optionValue];
      }

      setSelectedOptions(newSelectedOptions);
      setIsOpen(false); // Cerrar dropdown automáticamente

      // Actualizar filterValues y notificar al padre
      updateFilterValuesAndNotifyParent(newSelectedOptions);
    }
  };

  // Función auxiliar para actualizar filterValues y notificar al padre
  const updateFilterValuesAndNotifyParent = (newSelectedOptions) => {
    const compositions = filterData?.filter?.filterCompositions || [];

    // Si no hay opciones seleccionadas, limpiar el filtro
    if (newSelectedOptions.length === 0) {
      console.log(
        `🗑️ FilterDropdown - No hay opciones seleccionadas, limpiando filtro ${filterData?.filter?.id}`
      );

      setFilterValues({
        filterID: filterData?.filter?.id,
        filterCompositions: [],
        filterType: "dropdown",
        selectionTimeMode: "string",
      });

      // Notificar al padre que se limpió el filtro
      returnValues({
        filterID: filterData?.filter?.id,
        filterCompositions: [],
        cleared: true, // Indicar que se limpió completamente
      });
      return;
    }

    // Si hay opciones seleccionadas, actualizar filterValues
    console.log(
      `🔄 FilterDropdown - Actualizando filtro ${filterData?.filter?.id} con opciones:`,
      newSelectedOptions
    );

    setFilterValues((prevFilterValues) => {
      const updatedFilterValues = { ...prevFilterValues };

      compositions.forEach((composition) => {
        let action;

        if (isMultipleSelection) {
          // Para múltiple selección, enviar todas las opciones seleccionadas
          action = {
            id: composition.id,
            value: newSelectedOptions,
            displayValue: newSelectedOptions
              .map((opt) => {
                const option = options.find((o) => o.value === opt);
                return option?.label || opt;
              })
              .join(", "),
          };
        } else {
          // Para selección única, enviar la única opción seleccionada
          const selectedOption = options.find(
            (opt) => opt.value === newSelectedOptions[0]
          );
          action = {
            id: composition.id,
            value: newSelectedOptions[0],
            displayValue: selectedOption?.label || newSelectedOptions[0] || "",
          };
        }

        const indexValue = updatedFilterValues.filterCompositions.findIndex(
          (comp) => comp.id === composition.id
        );

        if (indexValue !== -1) {
          updatedFilterValues.filterCompositions[indexValue] = action;
        } else {
          updatedFilterValues.filterCompositions.push(action);
        }
      });

      // Notificar al padre con los valores actualizados
      setTimeout(() => {
        returnValues(updatedFilterValues);
      }, 0);

      return updatedFilterValues;
    });
  };

  // Manejar click en el contenedor de la opción
  const handleOptionClick = (optionValue, event) => {
    // Para selección múltiple, prevenir que se ejecute si el click viene del checkbox
    if (isMultipleSelection && event.target.type === "checkbox") {
      return;
    }
    handleOptionToggle(optionValue);
  };

  // Manejar cambio en el checkbox específicamente
  const handleCheckboxChange = (optionValue, event) => {
    event.stopPropagation(); // Prevenir propagación al contenedor padre
    handleOptionToggle(optionValue);
  };

  // Manejar click en el input
  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  // Manejar cambio en el input de búsqueda
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Función para limpiar todas las selecciones
  const handleClearAll = (fromParent = false) => {
    console.log(
      `🧹 FilterDropdown - handleClearAll llamado - fromParent: ${fromParent}`
    );
    console.log(`🧹 FilterDropdown - filterID: ${filterData?.filter?.id}`);

    setSelectedOptions([]);
    setSearchValue("");

    // Limpiar también el estado interno del filtro
    setFilterValues({
      filterID: filterData?.filter?.id,
      filterCompositions: [],
      filterType: "dropdown",
      selectionTimeMode: "string", // Para dropdown siempre es string
    });

    // Solo notificar al padre si no viene del trigger del padre
    if (!fromParent) {
      console.log(
        `📤 FilterDropdown - Llamando returnValues con cleared: true`
      );
      returnValues({
        filterID: filterData?.filter?.id,
        filterCompositions: [],
        cleared: true, // Flag para indicar que se limpió
      });
    } else {
      console.log(
        `⏭️ FilterDropdown - NO llamando returnValues (viene del padre)`
      );
    }
  };

  // Función para aplicar selección y cerrar dropdown
  const handleApplySelection = async () => {
    setIsOpen(false);
    console.log(
      "💾 FilterDropdown - Apply Selection clicked, aplicando solo este filtro específico"
    );
    console.log("Applied selections:", selectedOptions);

    // Ya no es necesario actualizar filterValues aquí porque handleOptionToggle
    // ya lo hace automáticamente cada vez que se selecciona/deselecciona una opción

    // 🆕 APLICAR SOLO ESTE FILTRO ESPECÍFICO
    // En lugar de llamar onApplyFilters (que aplica todos los filtros),
    // enviamos directamente los valores del filtro actual al padre
    if (filterValues.filterCompositions.length > 0) {
      console.log(
        "🚀 Aplicando solo filtro específico:",
        filterData?.filter?.id
      );
      console.log("Valores del filtro:", filterValues);

      // Notificar al padre con los valores actualizados y flag de aplicación individual
      returnValues({
        ...filterValues,
        applyIndividual: true, // Flag para indicar que se debe aplicar solo este filtro
      });
    } else {
      console.log("⚠️ No hay valores seleccionados para aplicar");
    }
  };

  // Ocultar el componente si no hay filas y está configurado para auto-ocultar
  // if (autoHideWhenNoRows && options.length === 0 && !loading) {
  if (
    autoHideWhenNoRows &&
    (options.length === 0 || options.length === 1) &&
    !loading
  ) {
    return null;
  }

  return (
    <div className="filter-container">
      <div className="filter-item-title">
        {!isGroup && (
          <button className="config-button">
            <span className="icon-more-horizontal"></span>
          </button>
        )}
        <span className="filter-title-text">
          {filterData?.filter?.filterName || filterData?.label || "Filter"}
        </span>
        <span style={{ flex: 1 }}></span>
        <span className="clear-all" onClick={() => handleClearAll()}>
          Clear All
        </span>
        {/* <button
                        className="btn-apply-selection"
                        style={{ marginRight: "5px" }}
                      >
                        Apply selection
                      </button> */}
      </div>

      {/* <div className="filter-item-clear-all">
          <span>Clear All</span>
        </div> */}

      {/* <div>
        <Dropdowns data={dropdown.config} />
      </div> */}

      <div className="filter-item-dropdown" ref={dropdownRef}>
        <div className="search-input-container">
          <input
            type="text"
            placeholder={`Search ${
              filterData?.filter?.filterName?.toLowerCase() || "options"
            }`}
            className="search-input"
            value={searchValue}
            onChange={handleSearchChange}
            onClick={handleInputClick}
          />
          <span className="icon-search search-icon"></span>
        </div>

        {isOpen && (
          <div className="dropdown-menu">
            {/* Solo mostrar botón Apply para selección múltiple */}
            {isMultipleSelection && (
              <div className="dropdown-header">
                <button
                  className="apply-selection-btn"
                  onClick={handleApplySelection}
                >
                  Apply Selection
                </button>
              </div>
            )}

            {/* Contenedor con scroll solo para las opciones */}
            <div className="dropdown-options-container">
              {loading ? (
                <div className="dropdown-loading">Loading options...</div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`dropdown-option ${
                      selectedOptions.includes(option.value) ? "selected" : ""
                    }`}
                    onClick={(event) => handleOptionClick(option.value, event)}
                  >
                    {/* Solo mostrar checkbox para selección múltiple */}
                    {isMultipleSelection && (
                      <div className="option-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedOptions.includes(option.value)}
                          onChange={(event) =>
                            handleCheckboxChange(option.value, event)
                          }
                        />
                      </div>
                    )}
                    <div className="option-label">
                      {/* <span className="option-code">{option.value}</span>- */}
                      <span className="option-name">{option.label}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="dropdown-no-results">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="filter-item-selected">
        {selectedOptions.length > 0 ? (
          <div className="selected-items">
            <span className="selected-count">
              {/* {selectedOptions.length} airport
              {selectedOptions.length !== 1 ? "s" : ""}  */}
              Selected
            </span>
            <div className="selected-tags">
              {selectedOptions.map((value) => {
                const option = options.find((opt) => opt.value === value);
                // Extraer solo la parte antes del guión (la abreviatura)
                // const displayText =
                //   option?.label?.split(" - ")[0] || option?.value || value;
                return (
                  <span key={value} className="selected-tag">
                    {option.label}
                    <button
                      className="remove-tag"
                      onClick={() => handleOptionToggle(value)}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        ) : (
          <span className="no-selection">Nothing selected</span>
        )}
      </div>
    </div>
  );
};
